import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiEdit, FiTrash2, FiArrowLeft, FiSettings, FiCheckCircle, FiCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/common';
import { getNotices, deleteNotice, pinNotice } from '../../services/api';
import { NOTICE_CATEGORIES } from './noticesConfig';
import styles from './ManageNoticesPage.module.css';

const ManageNoticesPage = () => {
  const { activeDerasarId } = useAuth();
  const navigate = useNavigate();

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [pinning, setPinning] = useState(null);

  const loadNotices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotices(activeDerasarId);
      setNotices(res.data);
    } catch {
      toast.error('Failed to load notices');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId]);

  useEffect(() => {
    if (!activeDerasarId) return;
    loadNotices();
  }, [activeDerasarId, loadNotices]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;
    setDeleting(id);
    try {
      await deleteNotice(id);
      setNotices((prev) => prev.filter((n) => n.id !== id));
      toast.success('Notice deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete notice');
    } finally {
      setDeleting(null);
    }
  };

  const handlePin = async (id) => {
    setPinning(id);
    try {
      await pinNotice(id);
      await loadNotices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update pin');
    } finally {
      setPinning(null);
    }
  };

  const getCategoryLabel = (cat) =>
    NOTICE_CATEGORIES.find((c) => c.value === cat)?.label || cat || 'Uncategorized';

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className={styles.container}>
      <PageHeader
        icon={<FiSettings />}
        title="Manage Notices"
        actions={(
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/derasar/notices')}>
            <FiArrowLeft /> Back
          </button>
        )}
      />

      {notices.length === 0 ? (
        <div className={styles.emptyState}><p>No notices to manage.</p></div>
      ) : (
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <div className={styles.colPin}>Pin</div>
            <div className={styles.colTitle}>Title</div>
            <div className={styles.colCategory}>Category</div>
            <div className={styles.colDate}>Posted</div>
            <div className={styles.colExpiry}>Expiry</div>
            <div className={styles.colStatus}>Status</div>
            <div className={styles.colActions}>Actions</div>
          </div>

          {notices.map((notice) => (
            <div key={notice.id} className={styles.tableRow}>
              <div className={styles.colPin}>
                <button
                  className={styles.pinBtn}
                  onClick={() => handlePin(notice.id)}
                  disabled={pinning === notice.id}
                  title={notice.isPinned ? 'Pinned to dashboard' : 'Pin to dashboard'}
                >
                  {notice.isPinned
                    ? <FiCheckCircle size={18} className={styles.pinActive} />
                    : <FiCircle size={18} className={styles.pinInactive} />
                  }
                </button>
              </div>
              <div className={styles.colTitle}>{notice.title}</div>
              <div className={styles.colCategory}>
                <span className={styles.badge}>{getCategoryLabel(notice.category)}</span>
              </div>
              <div className={styles.colDate}>
                {new Date(notice.createdAt).toLocaleDateString('en-IN')}
              </div>
              <div className={styles.colExpiry}>
                {notice.expiryDate ? new Date(notice.expiryDate).toLocaleDateString('en-IN') : '-'}
              </div>
              <div className={styles.colStatus}>
                <span className={notice.isActive ? styles.badgeActive : styles.badgeInactive}>
                  {notice.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className={styles.colActions}>
                <button
                  className={styles.actionBtn}
                  onClick={() => navigate(`/derasar/notices/${notice.id}/edit`)}
                  title="Edit notice"
                >
                  <FiEdit />
                </button>
                <button
                  className={styles.actionBtnDelete}
                  onClick={() => handleDelete(notice.id)}
                  disabled={deleting === notice.id}
                  title="Delete notice"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageNoticesPage;
